import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

const avatar = cva(
  'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-sunken font-medium text-text-muted select-none',
  {
    variants: {
      size: {
        sm: 'size-6 text-xs',
        md: 'size-9 text-sm',
        lg: 'size-12 text-base',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface AvatarProps extends VariantProps<typeof avatar> {
  /** The person or thing depicted. Used for the initials and the alt text. */
  name: string;
  src?: string | undefined;
  className?: string;
}

/**
 * A person's photo, or their initials when there is none.
 *
 * The image carries `alt=""` and is marked decorative even though it depicts a
 * person. That is deliberate: an avatar is always accompanied by the name in
 * text — in a table cell, a menu item, a byline — so a non-empty `alt` makes a
 * screen reader announce the same name twice in a row. The fallback initials
 * are hidden from the accessibility tree for the same reason.
 *
 * If you ever render this *without* an adjacent name, it needs a real label,
 * and the call site is wrong rather than this component.
 */
export function Avatar({ name, src, size, className }: AvatarProps) {
  return (
    <span className={cn(avatar({ size }), className)}>
      {src ? (
        <img src={src} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        <span aria-hidden="true">{initialsOf(name)}</span>
      )}
    </span>
  );
}

/**
 * Up to two initials, taken from the first and last word.
 *
 * Uses the code points rather than `charAt`, because an Arabic name and an
 * emoji-bearing display name both break a UTF-16 index — the latter produces
 * half a surrogate pair, which renders as a replacement character.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  const first = [...(words[0] ?? '')][0] ?? '';
  const last = words.length > 1 ? ([...(words[words.length - 1] ?? '')][0] ?? '') : '';

  return (first + last).toLocaleUpperCase();
}
